import React, { useEffect, useState } from "react";
import { fmtBytes, fmtNum } from "../utils/formatters";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useWanName, WanLabel } from "./WanNames";
import {
  TimeSeriesChart,
  ArcGauge,
  DonutChart,
  MiniKPI,
  SparkArea,
  QoSTimeline,
  useTimeSeries,
} from "./DiagCharts";

// ── Main Panel ───────────────────────────────────────────────────────────────

export default function DiagOverviewPanel({ nodeApiUrl, selectedNode }) {
  const wanName = useWanName();
  const [diag, setDiag] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [qosEvents, setQosEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const ts = useTimeSeries(60);

  useEffect(() => {
    if (!selectedNode) { setDiag(null); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);

    const doFetch = async () => {
      try {
        const reqs = ["/diagnostics", "/health", "/stats", "/qos/events"];
        const results = await Promise.all(
          reqs.map((p) => {
            const u = nodeApiUrl(p);
            return u
              ? fetchWithTimeout(u, { signal: controller.signal })
                  .then((r) => (r.ok ? r.json() : null))
                  .catch(() => null)
              : Promise.resolve(null);
          })
        );
        if (controller.signal.aborted) return;
        const [d, h, s, qe] = results;
        if (d) setDiag(d);
        if (h) {
          setHealthData(h);
          for (const w of h.health || []) {
            const n = w.wan || w.name;
            ts.push(n + "_lat", w.latency_ms ?? 0);
            ts.push(n + "_jit", w.jitter_ms ?? 0);
            ts.push(n + "_loss", (w.loss_ratio ?? 0) * 100);
            ts.push(n + "_bw", w.measured_bw_mbps ?? 0);
            ts.push(n + "_score", w.score_balanced ?? 0);
            ts.push(n + "_txr", (w.tx_bytes_per_sec ?? 0) / 1024);
            ts.push(n + "_rxr", (w.rx_bytes_per_sec ?? 0) / 1024);
          }
        }
        if (s) setStatsData(s);
        if (qe) setQosEvents(qe.events || []);
        setLastFetch(new Date());
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[DiagOverview]", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    doFetch();
    const timer = setInterval(doFetch, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]); // eslint-disable-line

  if (!selectedNode) {
    return (
      <div className="panel diag-panel">
        <div className="panel-title">System Overview</div>
        <div className="panel-sub">Select an edge node to view system diagnostics.</div>
      </div>
    );
  }

  const sys = diag?.system;
  const tun = diag?.tunnel;
  const tc = diag?.tc_classifier;
  const healthWans = healthData?.health || [];
  const wanNames = healthWans.map((w) => w.wan || w.name);

  const classSegments = statsData?.classes
    ? Object.entries(statsData.classes).map(([cls, cnt]) => ({ label: cls, value: cnt }))
    : [];

  return (
    <div className="panel diag-panel">
      <div className="panel-title">
        System Overview {diag ? "\u2014 " + selectedNode : ""}
      </div>
      <div className="panel-sub">
        {loading && !diag ? "Loading\u2026" : "Real-time health, performance charts, and system telemetry."}
        <span className="diag-refresh-ts">
          {lastFetch ? " Updated " + lastFetch.toLocaleTimeString() : "\u00A0"}
        </span>
      </div>

      <div className="diag-dashboard">

        {/* ── Row 1: System KPIs ── */}
        {sys && (
          <div className="diag-kpi-strip">
            <MiniKPI label="CPUs" value={sys.num_cpu} color="#3b82f6" />
            <MiniKPI label="Heap" value={sys.heap_mb != null ? sys.heap_mb.toFixed(1) : "\u2014"} unit=" MB" color="#8b5cf6" />
            <MiniKPI label="Goroutines" value={sys.goroutines || 0} color="#06b6d4" />
            <MiniKPI label="Uptime" value={sys.uptime || "\u2014"} color="#22c55e" />
            <MiniKPI label="IP Forward" value={sys.ip_forward ? "ON" : "OFF"} color={sys.ip_forward ? "#22c55e" : "#ef4444"} />
            <MiniKPI label="Restarts" value={sys.restart_count || 0} color={sys.restart_count > 0 ? "#ef4444" : "#22c55e"} />
          </div>
        )}

        {/* ── Row 2: Health Gauges + Latency ── */}
        <div className="diag-row cols-2">
          <div className="diag-card">
            <div className="diag-card-title">WAN Health Scores</div>
            <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 12 }}>
              {healthWans.map((w) => (
                <ArcGauge
                  key={w.wan || w.name}
                  value={Math.round(w.score_balanced ?? 0)}
                  max={100}
                  label={wanName(w.wan || w.name)}
                  size={130}
                />
              ))}
            </div>
          </div>
          <div className="diag-card">
            <div className="diag-card-title">Latency (3 min)</div>
            <TimeSeriesChart
              series={wanNames.map((n, i) => ({
                label: wanName(n) + " latency",
                data: ts.get(n + "_lat"),
                color: i === 0 ? "#6366f1" : "#06b6d4",
              }))}
              yLabel="ms"
              yMin={0}
              height={180}
            />
          </div>
        </div>

        {/* ── Row 3: Jitter + Loss ── */}
        <div className="diag-row cols-2">
          <div className="diag-card">
            <div className="diag-card-title">Jitter (3 min)</div>
            <TimeSeriesChart
              series={wanNames.map((n, i) => ({
                label: wanName(n) + " jitter",
                data: ts.get(n + "_jit"),
                color: i === 0 ? "#f59e0b" : "#ec4899",
              }))}
              yLabel="ms"
              yMin={0}
              height={160}
            />
          </div>
          <div className="diag-card">
            <div className="diag-card-title">Packet Loss (3 min)</div>
            <TimeSeriesChart
              series={wanNames.map((n, i) => ({
                label: wanName(n) + " loss",
                data: ts.get(n + "_loss"),
                color: i === 0 ? "#ef4444" : "#f97316",
              }))}
              yLabel="%"
              yMin={0}
              height={160}
            />
          </div>
        </div>

        {/* ── Row 4: Throughput + Classification Donut ── */}
        <div className="diag-row cols-2">
          <div className="diag-card">
            <div className="diag-card-title">{"Throughput \u2014 KB/s (3 min)"}</div>
            <TimeSeriesChart
              series={wanNames.flatMap((n, i) => [
                { label: wanName(n) + " TX", data: ts.get(n + "_txr"), color: i === 0 ? "#6366f1" : "#06b6d4" },
                { label: wanName(n) + " RX", data: ts.get(n + "_rxr"), color: i === 0 ? "#8b5cf6" : "#14b8a6" },
              ])}
              yLabel="KB/s"
              yMin={0}
              height={180}
            />
          </div>
          <div className="diag-card">
            <div className="diag-card-title">Traffic Classification</div>
            {classSegments.length > 0 ? (
              <DonutChart segments={classSegments} size={170} thickness={26} centerLabel="Packets" maxLegend={8} />
            ) : (
              <div className="diag-empty">No classification data yet.</div>
            )}
          </div>
        </div>

        {/* ── Row 5: BW Gauges + Score Trend ── */}
        <div className="diag-row cols-2">
          <div className="diag-card">
            <div className="diag-card-title">Measured Bandwidth</div>
            <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 12 }}>
              {healthWans.map((w) => (
                <ArcGauge
                  key={w.wan || w.name}
                  value={w.measured_bw_mbps != null ? parseFloat(w.measured_bw_mbps.toFixed(1)) : 0}
                  max={Math.max(100, ...healthWans.map((h) => h.measured_bw_mbps || 0)) * 1.2}
                  label={wanName(w.wan || w.name)}
                  unit=" Mbps"
                  size={130}
                  thresholds={[{ pct: 0, color: "#ef4444" }, { pct: 0.3, color: "#f59e0b" }, { pct: 0.6, color: "#22c55e" }]}
                />
              ))}
            </div>
          </div>
          <div className="diag-card">
            <div className="diag-card-title">Health Score Trend (3 min)</div>
            <TimeSeriesChart
              series={wanNames.map((n, i) => ({
                label: wanName(n) + " score",
                data: ts.get(n + "_score"),
                color: i === 0 ? "#22c55e" : "#3b82f6",
              }))}
              yLabel="Score"
              yMin={0}
              yMax={100}
              height={160}
            />
          </div>
        </div>

        {/* ── Row 6: Tunnel + Classifier KPIs ── */}
        <div className="diag-row cols-2">
          {tun && (
            <div className="diag-card">
              <div className="diag-card-title">Tunnel Aggregate</div>
              <div className="diag-kpi-strip">
                <MiniKPI label="TX Pkts" value={fmtNum(tun.tx_packets)} />
                <MiniKPI label="RX Pkts" value={fmtNum(tun.rx_packets)} />
                <MiniKPI label="TX Data" value={fmtBytes(tun.tx_bytes)} />
                <MiniKPI label="RX Data" value={fmtBytes(tun.rx_bytes)} />
              </div>
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <span className={tun.errors === 0 ? "pill-ok" : "pill-err"}>
                  {fmtNum(tun.errors)} errors
                </span>
              </div>
            </div>
          )}
          {tc && (
            <div className="diag-card">
              <div className="diag-card-title">Traffic Classifier</div>
              <div className="diag-kpi-strip">
                <MiniKPI label="Status" value={tc.alive ? "Running" : "Stopped"} color={tc.alive ? "#22c55e" : "#ef4444"} />
                <MiniKPI label="PID" value={tc.pid || "\u2014"} />
                <MiniKPI label="Packets" value={fmtNum(tc.packets)} />
                <MiniKPI label="Classified" value={fmtNum(tc.classified)} />
              </div>
              <div className="diag-kpi-strip" style={{ marginTop: 4 }}>
                <MiniKPI label="Active Flows" value={fmtNum(tc.flows)} color="#6366f1" />
                <MiniKPI label="BPF OK" value={fmtNum(tc.bpf_update_ok)} color="#22c55e" />
                <MiniKPI label="BPF Fail" value={fmtNum(tc.bpf_update_fail)} color={tc.bpf_update_fail > 0 ? "#ef4444" : "#22c55e"} />
              </div>
            </div>
          )}
        </div>

        {/* ── Row 7: QoS Timeline ── */}
        {qosEvents.length > 0 && (
          <div className="diag-card">
            <div className="diag-card-title">QoS WAN Switch Events</div>
            <QoSTimeline events={qosEvents} width={680} />
          </div>
        )}

        {/* ── Row 8: WAN Health Table with Sparklines ── */}
        {healthWans.length > 0 && (
          <div className="diag-card">
            <div className="diag-card-title">WAN Health Snapshot</div>
            <div className="diag-table-wrap">
              <table className="diag-table">
                <thead>
                  <tr>
                    <th>Interface</th>
                    <th>Latency</th>
                    <th>Jitter</th>
                    <th>Loss</th>
                    <th>BW (Mbps)</th>
                    <th>Score</th>
                    <th>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {healthWans.map((h) => {
                    const n = h.wan || h.name;
                    const scoreArr = ts.get(n + "_score");
                    return (
                      <tr key={n}>
                        <td className="wan-name"><WanLabel iface={n} /></td>
                        <td>{h.latency_ms != null ? h.latency_ms.toFixed(1) + " ms" : "\u2014"}</td>
                        <td>{h.jitter_ms != null ? h.jitter_ms.toFixed(1) + " ms" : "\u2014"}</td>
                        <td>
                          <span className={h.loss_ratio > 0.05 ? "pill-err" : "pill-ok"}>
                            {h.loss_ratio != null ? (h.loss_ratio * 100).toFixed(1) + "%" : "\u2014"}
                          </span>
                        </td>
                        <td>{h.measured_bw_mbps != null ? h.measured_bw_mbps.toFixed(1) : "\u2014"}</td>
                        <td>
                          <span className={"diag-score " + (h.score_balanced >= 70 ? "good" : h.score_balanced >= 40 ? "warn" : "bad")}>
                            {h.score_balanced != null ? h.score_balanced.toFixed(0) : "\u2014"}
                          </span>
                        </td>
                        <td>
                          <SparkArea
                            data={scoreArr}
                            width={80}
                            height={22}
                            color={h.score_balanced >= 70 ? "#22c55e" : h.score_balanced >= 40 ? "#f59e0b" : "#ef4444"}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Row 9: DHCP Leases ── */}
        {diag?.dhcp_leases && diag.dhcp_leases.length > 0 && (
          <div className="diag-card">
            <div className="diag-card-title">DHCP Leases ({diag.dhcp_leases.length})</div>
            <div className="diag-table-wrap">
              <table className="diag-table">
                <thead>
                  <tr><th>IP</th><th>MAC</th><th>Hostname</th><th>Expires</th></tr>
                </thead>
                <tbody>
                  {diag.dhcp_leases.map((l, i) => (
                    <tr key={l.ip + "-" + i}>
                      <td className="wan-name">{l.ip}</td>
                      <td>{l.mac || "\u2014"}</td>
                      <td>{l.hostname || "\u2014"}</td>
                      <td>{l.expiry ? new Date(l.expiry * 1000).toLocaleString() : "\u2014"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Row 10: Recent Events ── */}
        {diag?.recent_events && diag.recent_events.length > 0 && (
          <div className="diag-card">
            <div className="diag-card-title">Recent System Events ({diag.recent_events.length})</div>
            <div className="diag-events-list">
              {diag.recent_events.slice().reverse().slice(0, 50).map((ev, i) => (
                <div className="diag-event" key={i}>
                  <span className="event-level" style={{
                    color: ev.level === "error" ? "#dc2626" : ev.level === "warn" ? "#d97706" : "#16a34a",
                  }}>
                    {(ev.level || "info").toUpperCase()}
                  </span>
                  <span className="event-source">[{ev.source}]</span>
                  <span className="event-msg">{ev.message}</span>
                  <span className="event-time">
                    {ev.time ? new Date(ev.time).toLocaleTimeString() : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
